import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import { Spin } from 'antd';

const getQartodHtml = (result) => {
  const qartodResultMap = {
    1: { label: 'Pass', color: '#49AA19', border: '#80C45E' },
    2: { label: 'Not Evaluated', color: 'rgba(255, 255, 255, 0.8)', border: '#999' },
    3: { label: 'Suspect', color: '#D89614', border: '#E4B65B' },
    4: { label: 'Fail', color: '#DC4446', border: '#E77C7E' },
    9: { label: 'Missing', color: 'transparent', border: '#999' },
  };

  const { label, color, border } = qartodResultMap[result] || qartodResultMap[9];

  const iconHtml = `
    QARTOD:
    <span style="
      display: inline-block;
      background: ${color};
      width: 12px;
      height: 12px;
      border: 1px solid ${border};
      border-radius: 50%;
      margin-right: 2px;
      margin-left: 4px;
    "></span>
  `;

  return `<div style="display: flex; align-items: center; gap: 4px;">${iconHtml}<span>${label}</span></div>`;
};

const ParameterChart = (props) => {
  const {
    obs,
    qartod,
    timestamps,
    name,
    unit,
    getQartodResults,
    isLoading,
    selectedTimestamp,
    dataPointsVisible,
    linesVisible,
  } = props;

  const chartRef = useRef(null);

  const colors = ['#cd5327', '#cd5327', '#cd5327'];

  const validIndices = timestamps.reduce((acc, _, i) => {
    if (obs[i] != null) acc.push(i);
    return acc;
  }, []);

  const timestampData = validIndices.map((i) => timestamps[i] * 1000);

  const timeSeriesData = validIndices.map((i) => [timestamps[i] * 1000, obs[i]]);

  const qartodSeriesData = validIndices.map((i) => [timestamps[i] * 1000, qartod?.[i]]);

  const start = new Date(timestampData[0]);
  const end = new Date(timestampData[timestampData.length - 1]);
  const multiDay = (end - start) > 24 * 60 * 60 * 1000; // More than 24 hours

  const handleZrClick = (params) => {
    const { offsetX, offsetY } = params;

    const chartInstance = chartRef.current.getEchartsInstance();
    const [xValue] = chartInstance.convertFromPixel({ seriesIndex: 0 }, [offsetX, offsetY]);

    chartInstance.dispatchAction({
      type: 'showTip',
      seriesIndex: 1,
      dataIndex: xValue,
    });

    const closestIndex = timestampData.reduce((closestIdx, ts, idx) => (Math.abs(ts - xValue) < Math.abs(timestampData[closestIdx] - xValue) ? idx : closestIdx), 0);
    const tsInSeconds = timestampData[closestIndex] / 1000;

    getQartodResults(tsInSeconds);
  };

  const handleChartReady = (chartInstance) => {
    chartInstance.getZr().on('click', handleZrClick);
  };

  useEffect(() => {
    if (!selectedTimestamp || !chartRef?.current) return;

    const chart = chartRef.current.getEchartsInstance();

    // Convert timestamp to pixel
    const x = chart.convertToPixel({ xAxisIndex: 0 }, selectedTimestamp * 1000);

    // Get grid to limit line height
    const grid = chart.getModel().getComponent('grid').coordinateSystem.getRect();

    chart.setOption({
      graphic: [
        {
          type: 'line',
          id: 'selected-line',
          shape: {
            x1: x,
            y1: grid.y,
            x2: x,
            y2: grid.y + grid.height,
          },
          style: {
            stroke: '#fff',
            lineWidth: 2,
          },
          silent: true,
          z: 10,
          clip: true,
        },
      ],
    });
  }, [selectedTimestamp]);

  // TODO: abstract to component
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spin />
      </div>
    );
  }

  const timeDiffs = qartodSeriesData.map((d, i, arr) => {
    if (i === 0) return null;
    return d[0] - arr[i - 1][0]; // timestamp difference in ms
  }).filter(Boolean);

  const medianSpacing = timeDiffs.length
    ? timeDiffs.sort()[Math.floor(timeDiffs.length / 2)]
    : 60 * 1000; // fallback to 1 minute

  const option = {
    color: colors,
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
      enterable: true,
      formatter: (params) => {
        if (!Array.isArray(params) || params.length === 0) return '';

        const axisValue = params[0].axisValue || (Array.isArray(params[0].value) ? params[0].value[0] : null);
        const date = moment(Number(axisValue));
        const timeStr = date.format('MMMM Do YYYY, h:mm a');

        // Separate obs and qartod values
        const obsLine = params.find((p) => p.seriesName === name);
        const qartodLine = params.find((p) => p.seriesName !== name);

        const lines = [];

        if (obsLine) {
          const value = Array.isArray(obsLine.value) ? obsLine.value[1] : obsLine.value;
          lines.push(`${obsLine.seriesName}: ${value == null ? 'no data' : `${value}${unit}`}`);
        }

        if (qartodLine) {
          const value = Array.isArray(qartodLine.value) ? qartodLine.value[1] : qartodLine.value;
          lines.push(getQartodHtml(value));
        }

        return [`<strong>${timeStr}</strong>`, ...lines].join('<br/>');
      },
    },
    grid: {
      top: 20,
      right: 10,
      left: 0,
      bottom: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'time',
      axisLine: {
        show: true,
        lineStyle: {
          color: '#fff',
        },
      },
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        hideOverlap: true,
        formatter: (value) => {
          const date = new Date(Number(value));

          if (multiDay) {
            // Show both date and time
            return date.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            });
          }

          // Only show time
          return date.toLocaleString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
        },
      },
    },
    yAxis: [
      {
        type: 'value',
        show: false,
        position: 'right',
        max: 3,
        min: 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
      {
        type: 'value',
        name: '',
        position: 'left',
        alignTicks: true,
        min: (value) => {
          const range = value.max - value.min;
          return Math.floor(value.min - range * 0.4);
        },
        max: (value) => {
          const range = value.max - value.min;
          return Math.ceil(value.max + range * 0.2);
        },
        splitLine: { show: false },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#fff',
          },
        },
        axisLabel: {
          formatter: (value) => (Number.isInteger(value) ? `${value} ${unit}` : `${value.toFixed(2)} ${unit}`),
        },
      },
    ],
    series: [
      {
        name: 'QARTOD',
        type: 'custom',
        yAxisIndex: 0,
        renderItem: (params, api) => {
          const x = api.coord([api.value(0), 0])[0];
          const chartHeight = api.getHeight();
          const paddingBottom = 30;
          const y = chartHeight - paddingBottom;

          const pixelSpacing = api.coord([api.value(0) + medianSpacing, 0])[0] - x;
          const width = Math.max(pixelSpacing * 0.6, 2);

          const height = 16;

          const value = api.value(1);
          let color = 'transparent';
          if (value === 1) color = '#49AA19';
          else if (value === 2) color = 'rgba(255, 255, 255, 0.8)';
          else if (value === 3) color = '#D89614';
          else if (value === 4) color = '#DC4446';

          return {
            type: 'rect',
            shape: {
              x: x - width / 2,
              y: y - height / 2,
              width,
              height,
            },
            style: {
              fill: color,
            },
            // z: 10,
          };
        },
        data: qartodSeriesData,
        triggerLineEvent: true,
      },
      {
        name,
        type: 'line',
        symbol: dataPointsVisible ? 'circle' : 'none',
        symbolSize: 5,
        showSymbol: true,
        sampling: 'none',
        yAxisIndex: 1,
        lineStyle: {
          color: '#cd5327',
          width: linesVisible ? 2 : 0,
        },
        itemStyle: {
          color: '#cd5327',
        },
        // data: obs,
        data: timeSeriesData,
        triggerLineEvent: true,
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      ref={chartRef}
      onChartReady={handleChartReady}
    />
  );
};

ParameterChart.propTypes = {
  obs: PropTypes.array,
  qartod: PropTypes.array,
  timestamps: PropTypes.array,
  name: PropTypes.string,
  unit: PropTypes.string,
  getQartodResults: PropTypes.func,
  isLoading: PropTypes.bool,
  selectedTimestamp: PropTypes.number,
  dataPointsVisible: PropTypes.bool,
  linesVisible: PropTypes.bool,
};

ParameterChart.defaultProps = {
  obs: [],
  qartod: [],
  timestamps: [],
  name: '',
  unit: '',
  getQartodResults: () => {},
  isLoading: false,
  selectedTimestamp: undefined,
  dataPointsVisible: true,
  linesVisible: false,
};

// export default ParameterChart;
export default React.memo(ParameterChart);
